import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Activity, MailWarning, Cpu, AlertTriangle, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { DASH } from "@/constants/testIds";
import QueueHealthWidget from "@/components/admin/QueueHealthWidget";
import NetworkCostsWidget from "@/components/admin/NetworkCostsWidget";
import eventStream from "@/lib/eventStream";

function Kpi({ tid, label, value, sub, accent, icon: Icon }) {
  return (
    <div
      data-testid={DASH.kpi(tid)}
      className="border border-zinc-800 bg-zinc-900/40 p-5 rounded-sm relative group hover:border-zinc-700 transition-colors"
    >
      <div className="flex items-start justify-between">
        <div className="label-tiny">{label}</div>
        <Icon size={14} strokeWidth={1.5} className="text-zinc-500 group-hover:text-zinc-300 transition-colors" />
      </div>
      <div className="font-display text-5xl font-black mt-3 leading-none tracking-tight">
        <span className="mono" style={{ color: accent || "#fafafa" }}>{value}</span>
      </div>
      {sub && <div className="mt-2 text-xs text-zinc-500 font-mono">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [recent, setRecent] = useState(null); // live-updated via SSE

  useEffect(() => {
    api.get("/admin/dashboard").then(r => setD(r.data)).catch(e => setErr(e.response?.data?.detail || e.message));
  }, []);

  // Live "Recent Activity" via the shared event bus. Falls back to polling if SSE drops.
  useEffect(() => {
    const unsub = eventStream.subscribe(
      "recent_activity",
      (payload) => setRecent(payload),
      async () => {
        const r = await api.get("/admin/dashboard");
        return { items: r.data.recent_activity || [], generated_at: new Date().toISOString() };
      },
    );
    return unsub;
  }, []);

  if (err) {
    return <div className="p-6 text-red-400 font-mono text-sm">Error: {err}</div>;
  }
  if (!d) {
    return <div className="p-6 text-zinc-500 font-mono text-sm">Loading…</div>;
  }

  const fmt = (n) => new Intl.NumberFormat("en-US").format(n);
  const usd = (n) => "$" + (Number(n) || 0).toFixed(2);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">
          Network Overview
        </h1>
        <div className="text-zinc-500 text-sm mt-1">
          Real-time across <span className="mono text-zinc-300">{d.sites_count}</span> sites
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi tid="sites" label="Sites — Live / Total" value={`${d.sites_live}/${d.sites_count}`} sub={`${d.sites_count - d.sites_live} draft`} icon={Globe} />
        <Kpi tid="ai-spend" label="AI Spend Today" value={usd(d.ai_spend_today_usd)} sub="across all sites" accent="#f59e0b" icon={Cpu} />
        <Kpi tid="inbox" label="Unread Submissions" value={fmt(d.unread_inbox)} sub="forms + signups + unsubscribes" accent={d.unread_inbox > 0 ? "#10b981" : "#fafafa"} icon={MailWarning} />
        <Kpi tid="expiring" label="Domains Expiring 30d" value={fmt(d.expiring_domains?.length || 0)} sub="renewal required" accent={(d.expiring_domains?.length || 0) > 0 ? "#ef4444" : "#fafafa"} icon={AlertTriangle} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi tid="published" label="Articles Published" value={fmt(d.articles_published)} icon={FileText} />
        <Kpi tid="pending-review" label="Awaiting Approval" value={fmt(d.pending_review)} accent="#f59e0b" icon={FileText} />
        <Kpi tid="pending-ingest" label="In Ingest Queue" value={fmt(d.pending_ingest)} icon={Activity} />
      </div>

      <QueueHealthWidget />

      <NetworkCostsWidget />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div data-testid={DASH.recentActivity} className="border border-zinc-800 bg-zinc-900/40 rounded-sm">
          <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
            <div className="label-tiny">Recent Activity {recent && <span data-testid="recent-activity-live" className="ml-2 text-emerald-400">●</span>}</div>
            <Link to="/admin/moderation" className="text-xs text-zinc-400 hover:text-white">View queue →</Link>
          </div>
          <div className="divide-y divide-zinc-800">
            {((recent?.items) || d.recent_activity || []).slice(0, 8).map((a) => (
              <div key={a.id} className="px-5 py-2.5 flex items-center gap-3">
                <span className="dot bg-emerald-500" />
                <div className="text-sm text-zinc-200 truncate flex-1">{a.title}</div>
                <div className="mono text-[11px] text-zinc-500 shrink-0">{a.category_slug}</div>
              </div>
            ))}
            {((recent?.items) || d.recent_activity || []).length === 0 && (
              <div className="px-5 py-6 text-sm text-zinc-500">No published articles yet.</div>
            )}
          </div>
        </div>

        <div data-testid={DASH.expiring} className="border border-zinc-800 bg-zinc-900/40 rounded-sm">
          <div className="px-5 py-3 border-b border-zinc-800 label-tiny">Domain Status</div>
          <div className="divide-y divide-zinc-800">
            {(d.expiring_domains || []).length === 0 ? (
              <div className="px-5 py-6 text-sm text-zinc-500">No domains expiring in the next 30 days.</div>
            ) : (
              d.expiring_domains.map(x => (
                <div key={x.id} className="px-5 py-2.5 flex items-center gap-3">
                  <span className="dot bg-amber-500" />
                  <div className="text-sm text-zinc-200 truncate flex-1">{x.domain || x.name}</div>
                  <div className="mono text-[11px] text-zinc-500">{(x.expires_at || "").slice(0, 10)}</div>
                </div>
              ))
            )}
            <div className="px-5 py-3 flex items-center justify-between">
              <div className="text-xs text-zinc-500">Postmaster readings</div>
              <div className="mono text-[11px] text-zinc-300">{d.postmaster?.length || 0} domains</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
